import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useStripe } from '../contexts/StripeContext';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { auth } from '../firebase';

const SubscriptionPage = () => {
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { redirectToCheckout } = useStripe();
  const { isSubscribed, isLoading: checkingSubscription, syncWithStripe } = useSubscriptionStatus();

  const reason = searchParams.get('reason');

  useEffect(() => {
    if (!checkingSubscription && isSubscribed) {
      navigate('/app/emails');
    }
  }, [isSubscribed, checkingSubscription, navigate]);

  const handleContinue = async () => {
    setError(null);
    setIsLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');

      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

      const customerResponse = await fetch(`${API_URL}/create-customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid, email: user.email }),
      });

      if (!customerResponse.ok) {
        const errorData = await customerResponse.json();
        throw new Error(errorData.detail || 'Failed to set up billing');
      }

      const priceId = selectedPlan === 'yearly' ? 'annually' : 'monthly';

      const response = await fetch(`${API_URL}/createCheckoutSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid, priceId }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Checkout error');
      if (!data.sessionId) throw new Error('Missing checkout session');

      await redirectToCheckout(data.sessionId);
    } catch (err) {
      setError(err.message);
      console.error('Checkout error:', err);
      setIsLoading(false);
    }
  };

  const handleSyncSubscription = async () => {
    setIsLoading(true);
    try {
      await syncWithStripe();
      toast.success('Subscription synced');
    } catch (err) {
      toast.error('Could not sync. Try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (checkingSubscription) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#faf9f7]">
        <div className="w-5 h-5 border-2 border-gray-300 border-t-[#f97316] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf9f7] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
        className="w-full max-w-[480px]"
      >
        {/* Header */}
        <div className="mb-10">
          <span className="text-[11px] tracking-[0.3em] text-gray-400 font-light uppercase block mb-8">
            K L O R A H
          </span>

          <h1 className="text-[1.75rem] sm:text-[2rem] font-light tracking-tight text-gray-900 mb-2">
            {reason === 'subscription_required' ? 'Subscribe to continue' : 'Choose your plan'}
          </h1>
          <p className="text-[15px] text-gray-400">
            {reason === 'subscription_required'
              ? 'Your data is safe. Subscribe to pick up where you left off.'
              : 'Everything you need to run your business.'}
          </p>
        </div>

        {/* Error */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-3.5 bg-red-50 border border-red-100 rounded-xl"
          >
            <p className="text-[13px] text-red-600">{error}</p>
          </motion.div>
        )}

        {/* Plan cards */}
        <div className="space-y-3 mb-8">
          {/* Yearly */}
          <button
            onClick={() => setSelectedPlan('yearly')}
            className={`w-full text-left p-5 rounded-xl border-2 transition-all ${
              selectedPlan === 'yearly'
                ? 'border-[#f97316] bg-[#f97316]/[0.03]'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2.5">
                  <span className="text-[15px] font-medium text-gray-900">Yearly</span>
                  <span className="text-[11px] tracking-wider uppercase font-medium text-[#f97316] bg-[#f97316]/10 px-2 py-0.5 rounded-full">
                    Save $60
                  </span>
                </div>
                <p className="text-[13px] text-gray-400 mt-0.5">Billed annually</p>
              </div>
              <div className="text-right">
                <span className="text-[1.5rem] font-light text-gray-900">$20</span>
                <span className="text-[13px] text-gray-400">/mo</span>
              </div>
            </div>
          </button>

          {/* Monthly */}
          <button
            onClick={() => setSelectedPlan('monthly')}
            className={`w-full text-left p-5 rounded-xl border-2 transition-all ${
              selectedPlan === 'monthly'
                ? 'border-[#f97316] bg-[#f97316]/[0.03]'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <span className="text-[15px] font-medium text-gray-900">Monthly</span>
                <p className="text-[13px] text-gray-400 mt-0.5">Cancel anytime</p>
              </div>
              <div className="text-right">
                <span className="text-[1.5rem] font-light text-gray-900">$25</span>
                <span className="text-[13px] text-gray-400">/mo</span>
              </div>
            </div>
          </button>
        </div>

        {/* Includes */}
        <div className="mb-8 px-1">
          <p className="text-[12px] tracking-wider uppercase text-gray-400 mb-3">Includes</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {['AI assistant', 'Invoicing', 'Contracts', 'Client portal', 'Lead follow-ups', 'Scheduling'].map((feature) => (
              <span key={feature} className="text-[13px] text-gray-500">{feature}</span>
            ))}
          </div>
        </div>

        {/* Subscribe button */}
        <button
          onClick={handleContinue}
          disabled={isLoading}
          className="w-full py-4 bg-[#f97316] hover:bg-[#ea580c] text-white text-[15px] font-medium rounded-xl transition-colors disabled:opacity-50"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Processing
            </span>
          ) : (
            'Subscribe'
          )}
        </button>

        {/* Secondary actions */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => navigate('/app/profile')}
            className="text-[13px] text-gray-400 hover:text-gray-600 transition-colors"
          >
            Go to profile
          </button>
          <button
            onClick={handleSyncSubscription}
            disabled={isLoading}
            className="text-[13px] text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            Already subscribed? Sync
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default SubscriptionPage;
